import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  BarElement,
  LinearScale,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  ArrowRightOutlined,
  BarChartOutlined,
  FolderOpenOutlined,
  ReloadOutlined,
  TagsOutlined,
} from "@ant-design/icons";
import {
  Badge,
  Button,
  Card,
  Col,
  Empty,
  Row,
  Skeleton,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import * as pmService from "services/pmService";
import paths from "routes/paths";

ChartJS.register(
  ArcElement,
  CategoryScale,
  BarElement,
  LinearScale,
  Tooltip,
  Legend,
  Filler,
);

const { Title, Text } = Typography;

const formatDateTime = (value) => {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getProjectTitle = (project) => project?.name || project?.code || "Untitled project";

const getProjectTimestamp = (project) => {
  const value = project?.updatedAt || project?.createdAt || project?.updatedOn || project?.createdOn;
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
};

const getTaskTimestamp = (task) => {
  const value = task?.updatedAt || task?.createdAt || task?.updatedOn || task?.createdOn;
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
};

const countBy = (items, keyFn) =>
  items.reduce((acc, item) => {
    const key = keyFn(item);
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

const taskStatusConfig = {
  OPEN: { label: "Open", bg: "#dbeafe", color: "#1d4ed8" },
  IN_PROGRESS: { label: "In Progress", bg: "#ffedd5", color: "#c2410c" },
  COMPLETED: { label: "Completed", bg: "#dcfce7", color: "#15803d" },
  BLOCKED: { label: "Blocked", bg: "#fee2e2", color: "#b91c1c" },
};

const taskPriorityLabel = (priority) => {
  if (priority === 0) return "None";
  if (priority === 1) return "Low";
  if (priority === 2) return "Medium";
  if (priority === 3) return "High";
  return "Medium";
};

const taskPriorityConfig = (priority) => {
  const label = taskPriorityLabel(priority).toUpperCase();
  if (label === "HIGH") return { label: "High", bg: "#fee2e2", color: "#b91c1c" };
  if (label === "MEDIUM") return { label: "Medium", bg: "#fef3c7", color: "#c2410c" };
  if (label === "LOW") return { label: "Low", bg: "#dbeafe", color: "#1d4ed8" };
  return { label: "None", bg: "#e5e7eb", color: "#374151" };
};

const compactUserName = (task = {}) => {
  const assignee = task?.assignee || {};
  const raw =
    assignee?.fullName ||
    `${assignee?.firstName || ""} ${assignee?.lastName || ""}`.trim() ||
    task?.assigneeName ||
    task?.assigneeEmail ||
    "";
  return String(raw || "").trim() || "Unknown";
};

const MetricCard = ({ title, value, subtitle, icon, color, onClick }) => (
  <Card
    bordered={false}
    onClick={onClick}
    style={{
      borderRadius: 22,
      border: "1px solid #e5e7eb",
      boxShadow: "0 12px 28px rgba(15, 23, 42, 0.06)",
      cursor: onClick ? "pointer" : "default",
      height: "100%",
      minHeight: 126,
      overflow: "hidden",
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)",
    }}
    bodyStyle={{ padding: 18 }}
  >
    <div className="d-flex align-items-start justify-content-between gap-3 h-100">
      <div>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".08em", color: "#94a3b8", textTransform: "uppercase" }}>
          {title}
        </div>
        <div style={{ marginTop: 6, fontSize: 28, fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>
          {value}
        </div>
        <div style={{ marginTop: 8, color: "#64748b", fontSize: 12.5, lineHeight: 1.35 }}>{subtitle}</div>
      </div>
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 14,
          background: color,
          color: "#fff",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          boxShadow: "0 8px 18px rgba(15,23,42,0.12)",
        }}
      >
        {icon}
      </div>
    </div>
  </Card>
);

const SectionCard = ({ title, subtitle, extra, children, style, bodyStyle }) => (
  <Card
    bordered={false}
    style={{
      borderRadius: 20,
      border: "1px solid #e5e7eb",
      boxShadow: "0 12px 28px rgba(15,23,42,0.05)",
      ...style,
    }}
    bodyStyle={{ padding: 18, ...bodyStyle }}
    title={
      <div className="d-flex align-items-start justify-content-between gap-3">
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>{title}</div>
          {subtitle && <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>{subtitle}</div>}
        </div>
        {extra}
      </div>
    }
  >
    {children}
  </Card>
);

const PMDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [tags, setTags] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [recentTasks, setRecentTasks] = useState([]);

  const loadOverview = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setSyncing(true);

    try {
      const [projectsPage, tagsData] = await Promise.all([
        pmService.listProjects({ page: 0, size: 100 }),
        pmService.listTags(),
      ]);

      const projectItems = projectsPage?.content || [];
      const tagItems = Array.isArray(tagsData) ? tagsData : tagsData?.content || [];
      setProjects(projectItems);
      setTags(tagItems);
      setLoading(false);

      setTasksLoading(true);
      void (async () => {
        try {
          const taskResults = await Promise.allSettled(
            projectItems.map((project) =>
              pmService.listTasks(project.id, { page: 0, size: 1000 }),
            ),
          );
          const taskItems = taskResults.flatMap((result, index) => {
            if (result.status !== "fulfilled") return [];
            const projectId = projectItems[index]?.id;
            const items = result.value?.content || [];
            return items.map((task) => ({
              ...task,
              projectId: task.projectId || projectId,
            }));
          });

          setAllTasks(taskItems);
          setRecentTasks(
            [...taskItems]
              .sort((a, b) => getTaskTimestamp(b) - getTaskTimestamp(a))
              .slice(0, 8),
          );
        } catch (taskError) {
          console.error("Failed to load dashboard tasks", taskError);
          setAllTasks([]);
          setRecentTasks([]);
        } finally {
          setTasksLoading(false);
          setSyncing(false);
        }
      })();
    } catch (error) {
      console.error("Failed to load dashboard overview", error);
      setLoading(false);
      setTasksLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const taskStats = useMemo(() => {
    const statusBuckets = countBy(allTasks, (task) => String(task?.status || "OPEN").toUpperCase());
    const priorityBuckets = countBy(allTasks, (task) => {
      const value = Number(task?.priority);
      return Number.isFinite(value) ? value : 0;
    });

    return { statusBuckets, priorityBuckets };
  }, [allTasks]);

  const statusChartData = useMemo(
    () => ({
      labels: ["Open", "In Progress", "Completed", "Blocked"],
      datasets: [
        {
          data: [
            taskStats.statusBuckets.OPEN || 0,
            taskStats.statusBuckets.IN_PROGRESS || 0,
            taskStats.statusBuckets.COMPLETED || 0,
            taskStats.statusBuckets.BLOCKED || 0,
          ],
          backgroundColor: ["#2563eb", "#f59e0b", "#16a34a", "#ef4444"],
          borderWidth: 0,
          hoverOffset: 6,
        },
      ],
    }),
    [taskStats.statusBuckets],
  );

  const priorityChartData = useMemo(
    () => ({
      labels: ["None", "Low", "Medium", "High", "Critical"],
      datasets: [
        {
          label: "Tasks by Priority",
          data: [
            taskStats.priorityBuckets[0] || 0,
            taskStats.priorityBuckets[1] || 0,
            taskStats.priorityBuckets[2] || 0,
            taskStats.priorityBuckets[3] || 0,
            taskStats.priorityBuckets[4] || 0,
          ],
          backgroundColor: ["#6b7280", "#22d3ee", "#fbbf24", "#ef4444", "#111827"],
          borderRadius: 6,
        },
      ],
    }),
    [taskStats.priorityBuckets],
  );

  const recentProjects = useMemo(
    () =>
      [...projects]
        .sort((a, b) => getProjectTimestamp(b) - getProjectTimestamp(a))
        .slice(0, 8),
    [projects],
  );

  const totalTaskCount = useMemo(
    () => projects.reduce((sum, project) => sum + (Number(project?.taskCount) || 0), 0),
    [projects],
  );

  const currentTasks = allTasks;
  const completedTasks = taskStats.statusBuckets.COMPLETED || 0;
  const pendingTasks =
    (taskStats.statusBuckets.OPEN || 0) +
    (taskStats.statusBuckets.IN_PROGRESS || 0) +
    (taskStats.statusBuckets.BLOCKED || 0);

  const projectColumns = useMemo(
    () => [
      {
        title: "Project",
        key: "name",
        render: (_, record) => (
          <Button
            type="link"
            style={{ padding: 0, fontWeight: 700, textAlign: "left" }}
            onClick={() => navigate(paths.projectDetails(record.id))}
          >
            {getProjectTitle(record)}
          </Button>
        ),
      },
      {
        title: "Owner",
        dataIndex: "ownerName",
        key: "ownerName",
        render: (value) => value || "Not assigned",
      },
      {
        title: "Updated",
        dataIndex: "updatedAt",
        key: "updatedAt",
        render: (value, record) => formatDateTime(value || record.createdAt),
      },
    ],
    [navigate],
  );

  const recentTaskColumns = useMemo(
    () => [
      {
        title: "Title",
        key: "title",
        render: (_, record) => (
          <Space direction="vertical" size={0}>
            <Button
              type="link"
              style={{ padding: 0, fontWeight: 700, textAlign: "left", height: "auto" }}
              onClick={() =>
                navigate(paths.projectIterationDetails(record.projectId, record.iterationId || "_"), {
                  state: { openTaskId: record.id },
                })
              }
            >
              <span style={{ display: "inline-block", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {record.title || "Untitled task"}
              </span>
            </Button>
          </Space>
        ),
      },
      {
        title: "Status",
        key: "status",
        width: 110,
        render: (_, record) => {
          const cfg = taskStatusConfig[String(record.status || "").toUpperCase()] || {
            label: String(record.status || "Unknown"),
            bg: "#e5e7eb",
            color: "#374151",
          };
          return (
            <Tag
              style={{
                marginInlineEnd: 0,
                border: "none",
                background: cfg.bg,
                color: cfg.color,
                borderRadius: 999,
                padding: "2px 10px",
                fontWeight: 700,
              }}
            >
              {cfg.label}
            </Tag>
          );
        },
      },
      {
        title: "Priority",
        key: "priority",
        width: 100,
        render: (_, record) => {
          const cfg = taskPriorityConfig(record.priority);
          return (
            <Tag
              style={{
                marginInlineEnd: 0,
                border: "none",
                background: cfg.bg,
                color: cfg.color,
                borderRadius: 999,
                padding: "2px 10px",
                fontWeight: 700,
              }}
            >
              {cfg.label}
            </Tag>
          );
        },
      },
      {
        title: "Points",
        key: "points",
        width: 72,
        render: (_, record) => record.points ?? "—",
      },
      {
        title: "Assignee",
        key: "assignee",
        render: (_, record) => (
          <span title={record.assigneeEmail || "No assignee email available"}>
            {compactUserName(record)}
          </span>
        ),
      },
    ],
    [navigate],
  );

  const statCards = [
    {
      title: "Total Projects",
      value: projects.length,
      subtitle: "All projects in the workspace",
      icon: <FolderOpenOutlined />,
      color: "#2563eb",
    },
    {
      title: "Total Tasks",
      value: totalTaskCount || (tasksLoading ? "…" : currentTasks.length),
      subtitle: "total tasks across all projects",
      icon: <BarChartOutlined />,
      color: "#16a34a",
    },
    {
      title: "Completed",
      value: tasksLoading ? "…" : completedTasks,
      subtitle: "Finished tasks",
      icon: <ReloadOutlined />,
      color: "#f59e0b",
    },
    {
      title: "Pending",
      value: tasksLoading ? "…" : pendingTasks,
      subtitle: "Open, in progress, or blocked",
      icon: <TagsOutlined />,
      color: "#8b5cf6",
    },
    {
      title: "Tags",
      value: tags.length,
      subtitle: "Shared across the workspace",
      icon: <TagsOutlined />,
      color: "#0ea5e9",
    },
  ];

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom", labels: { usePointStyle: true, padding: 18 } },
    },
  };

  if (loading) {
    return (
      <div className="project-page">
        <Card style={{ borderRadius: 20, border: "1px solid #e5e7eb" }}>
          <Skeleton active paragraph={{ rows: 5 }} />
        </Card>
      </div>
    );
  }

  return (
    <div className="project-page">
      <Card
        bordered={false}
        style={{
          borderRadius: 24,
          border: "1px solid #e5e7eb",
          boxShadow: "0 16px 40px rgba(15,23,42,0.06)",
          background:
            "radial-gradient(circle at top right, rgba(37,99,235,0.12), transparent 30%), linear-gradient(135deg, rgba(255,255,255,0.98), rgba(248,250,252,0.96))",
          marginBottom: 20,
        }}
        bodyStyle={{ padding: 22 }}
      >
        <Row gutter={[20, 20]} align="middle" justify="space-between">
          <Col xs={24} lg={14}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: ".12em", color: "#94a3b8", textTransform: "uppercase" }}>
                Project Management
              </div>
              <Title level={2} style={{ margin: "6px 0 8px", fontSize: 30 }}>
                Dashboard
              </Title>
            </div>
          </Col>

          <Col xs={24} lg={10}>
            <Space wrap style={{ width: "100%", justifyContent: "flex-end" }}>
              {syncing && <Badge status="processing" text="Updating" />}
              <Button onClick={() => loadOverview(true)} icon={<ReloadOutlined />}>
                Refresh
              </Button>
              <Button type="primary" onClick={() => navigate(paths.projects)} icon={<ArrowRightOutlined />}>
                View projects
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <div
        className="mb-4"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 20,
        }}
      >
        {statCards.map((item) => (
          <MetricCard
            key={item.title}
            title={item.title}
            value={item.value}
            subtitle={item.subtitle}
            icon={item.icon}
            color={item.color}
            onClick={item.title === "Projects" ? () => navigate(paths.projects) : undefined}
          />
        ))}
      </div>

      <Row gutter={[16, 16]} className="mb-3">
        <Col xs={24} xl={14}>
          <SectionCard
            title="Tasks by Status"
            subtitle="A quick read on the work flowing through the workspace."
            extra={<Tag color="blue">{recentTasks.length} tasks</Tag>}
            bodyStyle={{ height: 360 }}
          >
            {tasksLoading ? (
              <Skeleton active paragraph={{ rows: 8 }} />
            ) : (
              <Doughnut
                data={statusChartData}
                options={{
                  ...chartOptions,
                  cutout: "62%",
                }}
              />
            )}
          </SectionCard>
        </Col>

        <Col xs={24} xl={10}>
          <SectionCard
            title="Tasks by Priority"
            subtitle="Shows where attention is concentrated right now."
            extra={<Tag color="geekblue">{recentTasks.length} tasks</Tag>}
            bodyStyle={{ height: 360 }}
          >
            {tasksLoading ? (
              <Skeleton active paragraph={{ rows: 8 }} />
            ) : (
              <div style={{ height: "100%" }}>
                <Bar
                  data={priorityChartData}
                  options={{
                    ...chartOptions,
                    maintainAspectRatio: false,
                    scales: {
                      y: { beginAtZero: true, ticks: { stepSize: 1 } },
                    },
                  }}
                />
              </div>
            )}
          </SectionCard>
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="mb-3">
        <Col xs={24} xl={14}>
          <SectionCard
            title="Recent Projects"
            subtitle="Most recently updated projects, ready to open."
            extra={<Button type="link" onClick={() => navigate(paths.projects)}>View all</Button>}
          >
            {loading ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : recentProjects.length === 0 ? (
              <Empty description="No projects found" />
            ) : (
              <Table
                rowKey="id"
                dataSource={recentProjects}
                columns={projectColumns}
                pagination={false}
                size="small"
                onRow={(record) => ({
                  onClick: () => navigate(paths.projectDetails(record.id)),
                  style: { cursor: "pointer" },
                })}
              />
            )}
          </SectionCard>
        </Col>

        <Col xs={24} xl={10}>
          <SectionCard
            title="Recent Tasks"
            subtitle="Most recently updated tasks in the workspace."
            extra={<Tag color="green">{recentTasks.length} shown</Tag>}
          >
            {tasksLoading ? (
              <Skeleton active paragraph={{ rows: 5 }} />
            ) : recentTasks.length === 0 ? (
              <Empty description="No tasks found" />
            ) : (
            <Table
              rowKey={(record) => `${record.projectId || "project"}-${record.id}`}
              dataSource={recentTasks}
              columns={recentTaskColumns}
              pagination={false}
              size="small"
              tableLayout="fixed"
              scroll={{ x: 760 }}
              onRow={(record) => ({
                onClick: () =>
                  navigate(paths.projectIterationDetails(record.projectId, record.iterationId || "_"), {
                    state: { openTaskId: record.id },
                  }),
                style: { cursor: "pointer" },
              })}
            />
            )}
          </SectionCard>
        </Col>
      </Row>
    </div>
  );
};

export default PMDashboard;
